import React from 'react';

/** Imperative handle every offer demo exposes so the parent card can replay it on hover. */
export interface OfferDemoHandle {
  replay: () => void;
}

/** The tiny honesty label every demo carries, matching the hero report's "Example" tag. */
export const ExampleTag: React.FC<{ label?: string }> = ({ label = 'Example' }) => (
  <span className="offerdemo__tag" aria-hidden="true">
    {label}
  </span>
);

/**
 * The demo's one accessible description, as plain visually-hidden text -
 * not `role="img"` + `aria-label` on the demo's own container. That role
 * tells assistive tech "this subtree is a single, non-interactive image",
 * which conflicts with the real, focusable Replay `<button>` inside it
 * (axe: `nested-interactive`). The container itself now carries no role
 * at all; this text is what a screen reader actually announces, and the
 * rest of the demo's markup stays `aria-hidden`.
 */
export const DemoDescription: React.FC<{ children: string }> = ({ children }) => (
  <span className="sr-only">{children}</span>
);

/**
 * A real, keyboard-reachable button that appears once a demo has finished
 * playing, so a visitor who cannot or does not want to hover can watch it
 * again. Relies on the site-wide `:focus-visible` ring - no extra styling
 * needed for that. Always rendered (with its space reserved) and toggled
 * with `visible` rather than mounted/unmounted, so its arrival never grows
 * the card and never causes a layout shift.
 */
export const ReplayButton: React.FC<{ onClick: () => void; what: string; visible: boolean }> = ({
  onClick,
  what,
  visible,
}) => (
  <button
    type="button"
    className={`offerdemo__replay${visible ? ' is-visible' : ''}`}
    onClick={onClick}
    tabIndex={visible ? 0 : -1}
    aria-hidden={visible ? undefined : true}
  >
    Replay
    <span className="sr-only"> {what} demo</span>
  </button>
);
